import {
  createColumn,
  createEnum,
  createGroup,
  createRelationship,
  createTable,
  findTableByName,
  GROUP_COLOR_PALETTE,
  type Column,
  type EnumDef,
  type Group,
  type ParseErrorInfo,
  type RefType,
  type Relationship,
  type Schema,
  type Table,
} from '@hyphen-db-studio/schema-core';
import { splitSettings, splitTopLevelBlocks, type RawBlock } from './tokenizer';

export interface ParseResult {
  schema: Schema;
  errors: ParseErrorInfo[];
  projectName?: string;
  databaseType?: string;
  projectNote?: string;
}

const REF_OP = /(<>|>|<|-)/;

function stripQuotes(value: string): string {
  const v = value.trim();
  if (
    (v.startsWith("'") && v.endsWith("'")) ||
    (v.startsWith('"') && v.endsWith('"')) ||
    (v.startsWith('`') && v.endsWith('`'))
  ) {
    return v.slice(1, -1);
  }
  return v;
}

function parseRefSide(side: string): { table: string; column: string } | null {
  const trimmed = side.trim();
  const idx = trimmed.lastIndexOf('.');
  if (idx === -1) return null;
  const tablePart = trimmed.slice(0, idx).trim();
  const column = trimmed.slice(idx + 1).trim().replace(/^"|"$/g, '');
  if (column.startsWith('(')) return null; // composite ref, unsupported in v1
  const segments = tablePart.split('.');
  const table = segments[segments.length - 1].replace(/^"|"$/g, '');
  return { table, column };
}

function parseRefExpression(expr: string, line: number, errors: ParseErrorInfo[]): { from: { table: string; column: string }; to: { table: string; column: string }; type: RefType } | null {
  const match = expr.match(REF_OP);
  if (!match) {
    errors.push({ message: `Could not find a relationship operator (>, <, -, <>) in "${expr}"`, line, column: 1 });
    return null;
  }
  const opIdx = match.index ?? -1;
  const left = expr.slice(0, opIdx);
  const type = match[1] as RefType;
  const right = expr.slice(opIdx + type.length);
  const from = parseRefSide(left);
  const to = parseRefSide(right);
  if (!from || !to) {
    errors.push({ message: `Malformed relationship "${expr}" (expected table.column ${type} table.column)`, line, column: 1 });
    return null;
  }
  return { from, to, type };
}

function applyColumnSetting(item: string, column: Column, line: number, errors: ParseErrorInfo[]): void {
  const colonIdx = item.indexOf(':');
  const key = (colonIdx === -1 ? item : item.slice(0, colonIdx)).trim().toLowerCase();
  const value = colonIdx === -1 ? '' : item.slice(colonIdx + 1).trim();

  switch (key) {
    case 'pk':
    case 'primary key':
      column.settings.pk = true;
      break;
    case 'unique':
      column.settings.unique = true;
      break;
    case 'not null':
      column.settings.notNull = true;
      break;
    case 'null':
      column.settings.notNull = false;
      break;
    case 'increment':
      column.settings.increment = true;
      break;
    case 'default':
      column.settings.default = value;
      break;
    case 'note':
      column.settings.note = stripQuotes(value);
      break;
    case 'ref':
      // inline ref settings are resolved by the caller, which has table context
      break;
    default:
      errors.push({ message: `Unknown column setting "${item}"`, line, column: 1 });
  }
}

function parseColumnLine(
  line: string,
  lineNumber: number,
  errors: ParseErrorInfo[],
): { column: Column; inlineRef?: { type: RefType; table: string; column: string } } | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const match = trimmed.match(
    /^("[^"]+"|[A-Za-z_][\w]*)\s+("[^"]+"|[A-Za-z_][\w.]*(?:\([^)]*\))?(?:\[\])?)\s*(?:\[(.*)\])?\s*$/,
  );
  if (!match) {
    errors.push({ message: `Could not parse column definition "${trimmed}"`, line: lineNumber, column: 1 });
    return null;
  }
  const [, rawName, rawType, rawSettings] = match;
  const name = rawName.replace(/^"|"$/g, '');
  const type = rawType.replace(/^"|"$/g, '');
  const column = createColumn(name, type);

  let inlineRef: { type: RefType; table: string; column: string } | undefined;
  if (rawSettings) {
    for (const item of splitSettings(rawSettings)) {
      if (/^ref\s*:/i.test(item)) {
        const value = item.slice(item.indexOf(':') + 1).trim();
        const refMatch = value.match(new RegExp(`^${REF_OP.source}\\s*(.+)$`));
        if (refMatch) {
          const side = parseRefSide(refMatch[2]);
          if (side) inlineRef = { type: refMatch[1] as RefType, table: side.table, column: side.column };
        }
        continue;
      }
      applyColumnSetting(item, column, lineNumber, errors);
    }
  }

  return { column, inlineRef };
}

function parseTableBlock(block: RawBlock, errors: ParseErrorInfo[]): { table: Table; inlineRefs: Array<{ type: RefType; from: { table: string; column: string }; to: { table: string; column: string } }> } {
  const headerName = block.header.split(/\s+as\s+/i)[0].trim().split(/\s+/)[0];
  const nameSegments = headerName.replace(/\[.*$/, '').trim().split('.');
  const name = nameSegments[nameSegments.length - 1].replace(/^"|"$/g, '');

  const table = createTable(name, []);
  table.columns = [];

  const inlineRefs: Array<{ type: RefType; from: { table: string; column: string }; to: { table: string; column: string } }> = [];

  let skipDepth = 0;
  for (let idx = 0; idx < block.bodyLines.length; idx += 1) {
    const raw = block.bodyLines[idx];
    const trimmed = raw.trim();
    const lineNumber = block.startLine + idx + 1;

    if (skipDepth > 0) {
      skipDepth += (raw.match(/\{/g)?.length ?? 0) - (raw.match(/\}/g)?.length ?? 0);
      continue;
    }
    if (!trimmed) continue;

    if (/^indexes\s*\{/i.test(trimmed)) {
      const opens = raw.match(/\{/g)?.length ?? 0;
      const closes = raw.match(/\}/g)?.length ?? 0;
      skipDepth = Math.max(opens - closes, 0);
      continue;
    }
    if (/^Note\s*:/i.test(trimmed)) {
      table.note = stripQuotes(trimmed.replace(/^Note\s*:/i, '').trim());
      continue;
    }

    const parsed = parseColumnLine(trimmed, lineNumber, errors);
    if (!parsed) continue;
    table.columns.push(parsed.column);
    if (parsed.inlineRef) {
      inlineRefs.push({
        type: parsed.inlineRef.type,
        from: { table: name, column: parsed.column.name },
        to: { table: parsed.inlineRef.table, column: parsed.inlineRef.column },
      });
    }
  }

  if (!table.columns.length) {
    table.columns.push(createColumn('id', 'uuid', { pk: true }));
  }

  return { table, inlineRefs };
}

function parseEnumBlock(block: RawBlock): EnumDef {
  const name = block.header.trim().split(/\s+/)[0];
  const en = createEnum(name);
  en.values = [];
  for (const raw of block.bodyLines) {
    const trimmed = raw.trim();
    if (!trimmed || /^Note\s*:/i.test(trimmed)) continue;
    const valueName = trimmed.replace(/\s*\[.*\]\s*$/, '').replace(/^"|"$/g, '');
    if (valueName) en.values.push({ id: `${name}_${valueName}`, name: valueName });
  }
  return en;
}

/** A TableGroup header is either a bare word (`TableGroup Authentication`) or a quoted,
 * space-containing name (`TableGroup "01 - Identity & Access"`) — the naive "first
 * whitespace-delimited token" split used for bare words truncates the latter badly. */
function parseGroupHeaderName(header: string): string {
  const trimmed = header.trim().replace(/\[.*$/, '').trim();
  if (trimmed.startsWith('"') || trimmed.startsWith("'")) {
    const quote = trimmed[0];
    const end = trimmed.indexOf(quote, 1);
    if (end !== -1) return trimmed.slice(1, end);
  }
  return trimmed.split(/\s+/)[0]?.replace(/^"|"$/g, '') ?? trimmed;
}

function parseTableGroupBlock(block: RawBlock, schema: Schema, colorIndex: number): Group {
  const name = parseGroupHeaderName(block.header);
  let explicitColor: string | undefined;
  const tableIds: string[] = [];

  for (const raw of block.bodyLines) {
    const trimmed = raw.trim().replace(/\s*\[.*\]\s*$/, '');
    if (!trimmed) continue;

    const colorMatch = trimmed.match(/^color\s*:\s*(.+)$/i);
    if (colorMatch) {
      const value = stripQuotes(colorMatch[1].trim());
      if (/^#?[0-9a-fA-F]{3,8}$/.test(value)) explicitColor = value.startsWith('#') ? value : `#${value}`;
      continue;
    }

    const table = findTableByName(schema, trimmed);
    if (table) tableIds.push(table.id);
  }

  const color = explicitColor ?? GROUP_COLOR_PALETTE[colorIndex % GROUP_COLOR_PALETTE.length];
  return createGroup(name, tableIds, color);
}

function resolveAndAddRelationship(
  schema: Schema,
  errors: ParseErrorInfo[],
  line: number,
  type: RefType,
  from: { table: string; column: string },
  to: { table: string; column: string },
): void {
  const fromTable = findTableByName(schema, from.table);
  const toTable = findTableByName(schema, to.table);
  if (!fromTable || !toTable) {
    errors.push({ message: `Relationship references unknown table ("${from.table}" or "${to.table}")`, line, column: 1 });
    return;
  }
  const fromColumn = fromTable.columns.find((c) => c.name.toLowerCase() === from.column.toLowerCase());
  const toColumn = toTable.columns.find((c) => c.name.toLowerCase() === to.column.toLowerCase());
  if (!fromColumn || !toColumn) {
    errors.push({ message: `Relationship references unknown column ("${from.column}" or "${to.column}")`, line, column: 1 });
    return;
  }
  schema.relationships.push(createRelationship(fromTable.id, fromColumn.id, toTable.id, toColumn.id, type));
}

export function parseDbml(source: string): ParseResult {
  const errors: ParseErrorInfo[] = [];
  const schema: Schema = { tables: [], relationships: [], enums: [], groups: [] };
  const blocks = splitTopLevelBlocks(source);

  let projectName: string | undefined;
  let databaseType: string | undefined;
  let projectNote: string | undefined;

  const deferredRefs: Array<{ block: RawBlock; expr: string }> = [];
  const deferredInlineRefs: Array<{ line: number; type: RefType; from: { table: string; column: string }; to: { table: string; column: string } }> = [];
  const deferredGroups: RawBlock[] = [];

  // Pass 1: tables & enums first, so refs / groups can resolve names afterward.
  for (const block of blocks) {
    if (block.keyword === 'Table') {
      const { table, inlineRefs } = parseTableBlock(block, errors);
      schema.tables.push(table);
      for (const ref of inlineRefs) {
        deferredInlineRefs.push({ line: block.startLine, ...ref });
      }
    } else if (block.keyword === 'Enum') {
      schema.enums.push(parseEnumBlock(block));
    } else if (block.keyword === 'Project') {
      projectName = block.header.trim().split(/\s+/)[0];
      for (const raw of block.bodyLines) {
        const trimmed = raw.trim();
        const dbMatch = trimmed.match(/^database_type\s*:\s*(.+)$/i);
        if (dbMatch) databaseType = stripQuotes(dbMatch[1]);
        const noteMatch = trimmed.match(/^Note\s*:\s*(.+)$/i);
        if (noteMatch) projectNote = stripQuotes(noteMatch[1]);
      }
    } else if (block.keyword === 'TableGroup') {
      deferredGroups.push(block);
    } else if (block.keyword === 'Ref') {
      if (block.hasBraces) {
        for (const raw of block.bodyLines) {
          const trimmed = raw.trim();
          if (trimmed) deferredRefs.push({ block, expr: trimmed });
        }
      } else {
        deferredRefs.push({ block, expr: block.header });
      }
    }
  }

  // Pass 2: relationships & groups, now that every table/column name is known.
  for (const { block, expr } of deferredRefs) {
    const parsed = parseRefExpression(expr, block.startLine, errors);
    if (parsed) resolveAndAddRelationship(schema, errors, block.startLine, parsed.type, parsed.from, parsed.to);
  }
  for (const ref of deferredInlineRefs) {
    resolveAndAddRelationship(schema, errors, ref.line, ref.type, ref.from, ref.to);
  }
  for (const [groupIndex, block] of deferredGroups.entries()) {
    const group = parseTableGroupBlock(block, schema, groupIndex);
    schema.groups.push(group);
    for (const tableId of group.tableIds) {
      const table = schema.tables.find((t) => t.id === tableId);
      if (table) table.groupId = group.id;
    }
  }

  return { schema, errors, projectName, databaseType, projectNote };
}
